import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
    Flex,
    Text,
    Image,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@spothero/ui';
import {
    PROPTYPE_RESERVATION_VEHICLE,
    PROPTYPE_RESERVATION_PAYMENT_STATUS,
    PROPTYPE_RESERVATION_PARTNER,
} from '@/proptypes/reservation-prop-types';
import VehicleDisplay from '../vehicle-display';
import useVehicleChangeProps from 'pages/parking-pass/hooks/useVehicleChangeProps';
import ReservationUtils from 'utils/reservation-utils';

const VehicleDetails = ({
    vehicle,
    vehicleRequired,
    paymentStatus,
    activeStatus,
    enteredAndCantChange,
    partner,
}) => {
    const [showModal, setShowModal] = useState(false);
    const toggleInfoModal = () => setShowModal(prevShowModal => !prevShowModal);
    const buttonProps = useVehicleChangeProps();
    const canEdit =
        paymentStatus !== ReservationUtils.PAYMENT_STATUSES.REFUND &&
        activeStatus !== ReservationUtils.ACTIVE_STATUS.ENDED;
    const hideMakeModel = partner && partner?.updateVehicleEnabled === false;

    if (!vehicleRequired) {
        return null;
    }

    const changeVehicleButton = () => {
        if (enteredAndCantChange) {
            return (
                <Button
                    color="gray.dark"
                    _hover={{
                        color: 'gray.dark',
                    }}
                    sx={{
                        '@media print': {
                            display: 'none',
                        },
                    }}
                    variant="tertiary"
                    whiteSpace="nowrap"
                    marginLeft={2}
                    fontWeight="semibold"
                    fontFamily="body"
                    data-testid="VehicleDetails-button-disabled-edit"
                    onClick={toggleInfoModal}
                >
                    {vehicle || hideMakeModel ? 'Change' : 'Add Vehicle'}
                </Button>
            );
        } else {
            return (
                <Button
                    {...buttonProps}
                    sx={{
                        '@media print': {
                            display: 'none',
                        },
                    }}
                    variant="tertiary"
                    whiteSpace="nowrap"
                    marginLeft={2}
                    fontWeight="semibold"
                    fontFamily="body"
                    data-testid="VehicleDetails-button-edit"
                >
                    {vehicle || hideMakeModel ? 'Change' : 'Add Vehicle'}
                </Button>
            );
        }
    };

    return (
        <>
            <Flex
                borderBottomWidth="1px"
                borderColor="gray.100"
                padding={4}
                justifyContent="space-between"
                alignItems="center"
            >
                <Flex minWidth={0}>
                    <Image
                        src="/img/parking-pass/car.svg"
                        width={6}
                        height="auto"
                        marginRight={2.5}
                        color="gray.dark"
                    />
                    {vehicle ? (
                        <VehicleDisplay
                            hideMakeModel={hideMakeModel}
                            make={vehicle.makeLabel}
                            model={vehicle.modelLabel}
                            plate={vehicle.licensePlateValue}
                        />
                    ) : (
                        <Text color="error">
                            {hideMakeModel
                                ? 'Missing License Plate'
                                : 'Missing Vehicle'}
                        </Text>
                    )}
                </Flex>
                {canEdit && changeVehicleButton()}
            </Flex>
            <Modal
                hasHeader
                hasFooter
                size="sm"
                isOpen={showModal}
                onClose={toggleInfoModal}
                data-testid="VehicleDetails-modal-no-extensions-info"
            >
                <ModalHeader fontSize="lg">No changes after entry</ModalHeader>
                <ModalBody>
                    <Text>
                        <strong>
                            Time & vehicle changes are not allowed at this
                            facility after entry.
                        </strong>{' '}
                        You won&apos;t be refunded for leaving early and you may
                        be charged extra if you leave late.
                    </Text>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={toggleInfoModal}
                        variant="primary"
                        width="100%"
                    >
                        Okay
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

VehicleDetails.propTypes = {
    vehicle: PROPTYPE_RESERVATION_VEHICLE,
    vehicleRequired: PropTypes.bool.isRequired,
    activeStatus: PropTypes.oneOf(
        Object.values(ReservationUtils.ACTIVE_STATUS)
    ),
    paymentStatus: PROPTYPE_RESERVATION_PAYMENT_STATUS,
    enteredAndCantChange: PropTypes.bool,
    partner: PROPTYPE_RESERVATION_PARTNER,
};

export default VehicleDetails;
